import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CaseContact = ({ caseId, caseData, onRefresh }) => {
  const navigate = useNavigate();
  const [contact, setContact] = useState(caseData?.client_contact || null);
  const [isSearching, setIsSearching] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [enablingPortal, setEnablingPortal] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Editable contact fields
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    primary_email_address: '',
    secondary_email_address: '',
    primary_phone_number: '',
    secondary_phone_number: '',
    company: '',
    title: '',
    primary_address_street: '',
    primary_address_city: '',
    primary_address_province: '',
    primary_address_postal_code: '',
    primary_address_country: '',
    secondary_address_street: '',
    secondary_address_city: '',
    secondary_address_province: '',
    secondary_address_postal_code: '',
    secondary_address_country: '',
    is_client: false,
    client_status: '',
    contact_type: '',
  });

  useEffect(() => {
    setContact(caseData?.client_contact || null);
  }, [caseData]);

  useEffect(() => {
    // Initialize edit form when contact changes
    if (contact) {
      setEditForm({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        primary_email_address: contact.primary_email_address || '',
        secondary_email_address: contact.secondary_email_address || '',
        primary_phone_number: contact.primary_phone_number || '',
        secondary_phone_number: contact.secondary_phone_number || '',
        company: contact.company || '',
        title: contact.title || '',
        primary_address_street: contact.primary_address_street || '',
        primary_address_city: contact.primary_address_city || '',
        primary_address_province: contact.primary_address_province || '',
        primary_address_postal_code: contact.primary_address_postal_code || '',
        primary_address_country: contact.primary_address_country || '',
        secondary_address_street: contact.secondary_address_street || '',
        secondary_address_city: contact.secondary_address_city || '',
        secondary_address_province: contact.secondary_address_province || '',
        secondary_address_postal_code: contact.secondary_address_postal_code || '',
        secondary_address_country: contact.secondary_address_country || '',
        is_client: contact.is_client || false,
        client_status: contact.client_status || '',
        contact_type: contact.contact_type || '',
      });
    }
  }, [contact]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchContacts();
      }, 300);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm]);

  const searchContacts = async () => {
    try {
      setSearching(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/contacts/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchTerm, page_size: 10 }
      });
      setSearchResults(response.data.contacts || []);
    } catch (err) {
      console.error('Error searching contacts:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectContact = async (selectedContact) => {
    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');

      await axios.patch(`${API_URL}/cases/${caseId}/contact`, {
        client_contact_id: selectedContact.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setContact(selectedContact);
      setIsSearching(false);
      setSearchTerm('');
      setSearchResults([]);
      setSuccess('Contact assigned successfully');
      setTimeout(() => setSuccess(null), 3000);

      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveContact = async () => {
    if (!window.confirm('Are you sure you want to remove the contact from this case?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');

      await axios.patch(`${API_URL}/cases/${caseId}/contact`, {
        client_contact_id: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setContact(null);
      setSuccess('Contact removed from case');
      setTimeout(() => setSuccess(null), 3000);

      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove contact');
    } finally {
      setSaving(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveContactDetails = async () => {
    if (!contact?.id) return;

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');

      // Build the update payload
      const updatePayload = {
        first_name: editForm.first_name || null,
        last_name: editForm.last_name || null,
        primary_email_address: editForm.primary_email_address || null,
        secondary_email_address: editForm.secondary_email_address || null,
        primary_phone_number: editForm.primary_phone_number || null,
        secondary_phone_number: editForm.secondary_phone_number || null,
        company: editForm.company || null,
        title: editForm.title || null,
        primary_address_street: editForm.primary_address_street || null,
        primary_address_city: editForm.primary_address_city || null,
        primary_address_province: editForm.primary_address_province || null,
        primary_address_postal_code: editForm.primary_address_postal_code || null,
        primary_address_country: editForm.primary_address_country || null,
        secondary_address_street: editForm.secondary_address_street || null,
        secondary_address_city: editForm.secondary_address_city || null,
        secondary_address_province: editForm.secondary_address_province || null,
        secondary_address_postal_code: editForm.secondary_address_postal_code || null,
        secondary_address_country: editForm.secondary_address_country || null,
        is_client: editForm.is_client,
        client_status: editForm.client_status || null,
        contact_type: editForm.contact_type || null,
      };

      // Call the contacts API to update the contact
      const response = await axios.put(`${API_URL}/contacts/${contact.id}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state with response
      setContact(response.data);
      setIsEditingDetails(false);
      setSuccess('Contact updated successfully. Changes synced to Contacts module.');
      setTimeout(() => setSuccess(null), 3000);

      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form to current contact values
    if (contact) {
      setEditForm({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        primary_email_address: contact.primary_email_address || '',
        secondary_email_address: contact.secondary_email_address || '',
        primary_phone_number: contact.primary_phone_number || '',
        secondary_phone_number: contact.secondary_phone_number || '',
        company: contact.company || '',
        title: contact.title || '',
        primary_address_street: contact.primary_address_street || '',
        primary_address_city: contact.primary_address_city || '',
        primary_address_province: contact.primary_address_province || '',
        primary_address_postal_code: contact.primary_address_postal_code || '',
        primary_address_country: contact.primary_address_country || '',
        secondary_address_street: contact.secondary_address_street || '',
        secondary_address_city: contact.secondary_address_city || '',
        secondary_address_province: contact.secondary_address_province || '',
        secondary_address_postal_code: contact.secondary_address_postal_code || '',
        secondary_address_country: contact.secondary_address_country || '',
        is_client: contact.is_client || false,
        client_status: contact.client_status || '',
        contact_type: contact.contact_type || '',
      });
    }
    setIsEditingDetails(false);
  };

  const handleViewContact = () => {
    if (contact?.id) {
      navigate(`/contacts?id=${contact.id}`);
    }
  };

  const handleEnablePortal = async () => {
    if (!contact?.id) return;

    if (!contact.primary_email_address) {
      setError('Contact must have an email address to enable portal access');
      return;
    }

    try {
      setEnablingPortal(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await axios.post(`${API_URL}/cases/contacts/${contact.id}/portal-enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local contact state
      setContact(prev => ({
        ...prev,
        portal_enabled: true
      }));

      const message = response.data.email_sent
        ? `Portal enabled! An invitation email has been sent to ${contact.primary_email_address}`
        : 'Portal enabled! Please ask the client to check their email for the setup link.';
      setSuccess(message);
      setTimeout(() => setSuccess(null), 5000);

      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to enable portal');
    } finally {
      setEnablingPortal(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!contact?.id) return;

    try {
      setSendingPasswordReset(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await axios.post(`${API_URL}/cases/contacts/${contact.id}/portal-reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const message = response.data.email_sent
        ? `Password reset link sent to ${contact.primary_email_address}`
        : 'Failed to send password reset email. Please try again.';
      setSuccess(message);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send password reset');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  // Render editable input field
  const renderEditField = (label, field, placeholder = '') => (
    <div>
      <label className="block text-xs text-virgo-teal-500 mb-1">{label}</label>
      <input
        type="text"
        value={editForm[field] || ''}
        onChange={(e) => handleEditChange(field, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-600 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
      />
    </div>
  );

  // Render display field (non-editable)
  const renderDisplayField = (label, value) => (
    <div>
      <label className="text-xs text-virgo-teal-500">{label}</label>
      <p className="text-white">{value || '—'}</p>
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Case Contact</h2>
        <div className="flex gap-2">
          {contact && !isSearching && !isEditingDetails && (
            <>
              <button
                onClick={handleViewContact}
                className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors"
              >
                View in Contacts
              </button>
              <button
                onClick={() => setIsEditingDetails(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Edit Contact
              </button>
              <button
                onClick={() => setIsSearching(true)}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
              >
                Change Contact
              </button>
            </>
          )}
          {contact && isEditingDetails && (
            <>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="px-4 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveContactDetails}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
          {!contact && !isSearching && (
            <button
              onClick={() => setIsSearching(true)}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Assign Contact
            </button>
          )}
          {isSearching && (
            <button
              onClick={() => {
                setIsSearching(false);
                setSearchTerm('');
                setSearchResults([]);
              }}
              className="px-4 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-600/50 rounded-lg text-red-400">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-900/30 border border-green-600/50 rounded-lg text-green-400">
          {success}
        </div>
      )}

      {/* Contact Search (when searching for new contact) */}
      {isSearching && (
        <div className="mb-6 relative">
          <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
            Search for a contact
          </label>
          <input
            ref={searchRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Type to search contacts by name, email, or company..."
            className="w-full px-4 py-3 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white placeholder-virgo-teal-600 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            autoFocus
          />

          {/* Search Results Dropdown */}
          {(searchResults.length > 0 || searching) && (
            <div className="absolute z-10 w-full mt-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg shadow-xl max-h-80 overflow-y-auto">
              {searching ? (
                <div className="p-4 text-center text-virgo-teal-400">
                  Searching...
                </div>
              ) : (
                searchResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => handleSelectContact(result)}
                    disabled={saving}
                    className="w-full text-left px-4 py-3 hover:bg-virgo-navy-700 border-b border-virgo-navy-700 last:border-b-0 transition-colors"
                  >
                    <div className="font-medium text-white">{result.contact_name}</div>
                    <div className="text-sm text-virgo-teal-400">
                      {result.primary_email_address && <span>{result.primary_email_address}</span>}
                      {result.primary_email_address && result.company && <span> | </span>}
                      {result.company && <span>{result.company}</span>}
                    </div>
                    {result.is_client && (
                      <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${
                        result.client_status === 'active' ? 'bg-green-500/20 text-green-400' :
                        result.client_status === 'prospective' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {result.client_status || 'Client'}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Current Contact Display */}
      {contact ? (
        <div className="bg-virgo-navy-800/50 rounded-lg border border-virgo-teal-700/30">
          {/* Contact Header */}
          <div className="p-6 border-b border-virgo-navy-600">
            <div className="flex items-start justify-between">
              <div>
                {isEditingDetails ? (
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {renderEditField('First Name', 'first_name')}
                    {renderEditField('Last Name', 'last_name')}
                    {renderEditField('Company', 'company')}
                    {renderEditField('Title', 'title')}
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-white">{contact.contact_name}</h3>
                    {contact.company && (
                      <p className="text-virgo-teal-400 mt-1">{contact.title ? `${contact.title} at ` : ''}{contact.company}</p>
                    )}
                  </>
                )}
                <div className="flex gap-2 mt-2">
                  {isEditingDetails ? (
                    <div className="flex gap-4 items-center">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editForm.is_client}
                          onChange={(e) => handleEditChange('is_client', e.target.checked)}
                          className="rounded bg-virgo-navy-700 border-virgo-teal-600 text-cyan-500 focus:ring-cyan-500"
                        />
                        <span className="text-sm text-virgo-teal-300">Is Client</span>
                      </label>
                      {editForm.is_client && (
                        <select
                          value={editForm.client_status}
                          onChange={(e) => handleEditChange('client_status', e.target.value)}
                          className="px-3 py-1 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-lg text-white text-sm"
                        >
                          <option value="">Select Status</option>
                          <option value="active">Active</option>
                          <option value="prospective">Prospective</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      )}
                      <select
                        value={editForm.contact_type}
                        onChange={(e) => handleEditChange('contact_type', e.target.value)}
                        className="px-3 py-1 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-lg text-white text-sm"
                      >
                        <option value="">Contact Type</option>
                        <option value="Person">Person</option>
                        <option value="Company">Company</option>
                      </select>
                    </div>
                  ) : (
                    <>
                      {contact.is_client && (
                        <span className={`text-xs px-2 py-1 rounded ${
                          contact.client_status === 'active' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                          contact.client_status === 'prospective' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                          'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                        }`}>
                          {contact.client_status ? contact.client_status.charAt(0).toUpperCase() + contact.client_status.slice(1) : 'Client'}
                        </span>
                      )}
                      {contact.contact_type && (
                        <span className="text-xs px-2 py-1 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                          {contact.contact_type}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              {!isSearching && !isEditingDetails && (
                <button
                  onClick={handleRemoveContact}
                  disabled={saving}
                  className="text-red-400 hover:text-red-300 text-sm"
                >
                  Remove from case
                </button>
              )}
            </div>
          </div>

          {/* Contact Details Grid */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div>
              <h4 className="text-sm font-semibold text-virgo-gold-400 uppercase tracking-wider mb-4">Contact Information</h4>
              {isEditingDetails ? (
                <div className="space-y-3">
                  {renderEditField('Primary Email', 'primary_email_address')}
                  {renderEditField('Secondary Email', 'secondary_email_address')}
                  {renderEditField('Primary Phone', 'primary_phone_number')}
                  {renderEditField('Secondary Phone', 'secondary_phone_number')}
                </div>
              ) : (
                <div className="space-y-3">
                  {renderDisplayField('Primary Email', contact.primary_email_address)}
                  {contact.secondary_email_address && renderDisplayField('Secondary Email', contact.secondary_email_address)}
                  {renderDisplayField('Primary Phone', contact.primary_phone_number)}
                  {contact.secondary_phone_number && renderDisplayField('Secondary Phone', contact.secondary_phone_number)}
                </div>
              )}
            </div>

            {/* Address */}
            <div>
              <h4 className="text-sm font-semibold text-virgo-gold-400 uppercase tracking-wider mb-4">Primary Address</h4>
              {isEditingDetails ? (
                <div className="space-y-3">
                  {renderEditField('Street', 'primary_address_street')}
                  <div className="grid grid-cols-2 gap-3">
                    {renderEditField('City', 'primary_address_city')}
                    {renderEditField('Province/State', 'primary_address_province')}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {renderEditField('Postal Code', 'primary_address_postal_code')}
                    {renderEditField('Country', 'primary_address_country')}
                  </div>
                </div>
              ) : (
                contact.primary_address_street || contact.primary_address_city ? (
                  <div className="text-white">
                    {contact.primary_address_street && <p>{contact.primary_address_street}</p>}
                    {(contact.primary_address_city || contact.primary_address_province || contact.primary_address_postal_code) && (
                      <p>
                        {contact.primary_address_city}{contact.primary_address_city && contact.primary_address_province ? ', ' : ''}
                        {contact.primary_address_province} {contact.primary_address_postal_code}
                      </p>
                    )}
                    {contact.primary_address_country && <p>{contact.primary_address_country}</p>}
                  </div>
                ) : (
                  <p className="text-virgo-teal-500">No address on file</p>
                )
              )}

              {/* Secondary Address */}
              {(isEditingDetails || contact.secondary_address_street || contact.secondary_address_city) && (
                <>
                  <h4 className="text-sm font-semibold text-virgo-gold-400 uppercase tracking-wider mb-4 mt-6">Secondary Address</h4>
                  {isEditingDetails ? (
                    <div className="space-y-3">
                      {renderEditField('Street', 'secondary_address_street')}
                      <div className="grid grid-cols-2 gap-3">
                        {renderEditField('City', 'secondary_address_city')}
                        {renderEditField('Province/State', 'secondary_address_province')}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {renderEditField('Postal Code', 'secondary_address_postal_code')}
                        {renderEditField('Country', 'secondary_address_country')}
                      </div>
                    </div>
                  ) : (
                    <div className="text-white">
                      {contact.secondary_address_street && <p>{contact.secondary_address_street}</p>}
                      {(contact.secondary_address_city || contact.secondary_address_province || contact.secondary_address_postal_code) && (
                        <p>
                          {contact.secondary_address_city}{contact.secondary_address_city && contact.secondary_address_province ? ', ' : ''}
                          {contact.secondary_address_province} {contact.secondary_address_postal_code}
                        </p>
                      )}
                      {contact.secondary_address_country && <p>{contact.secondary_address_country}</p>}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Portal Status */}
          {contact.is_client && !isEditingDetails && (
            <div className="px-6 pb-6">
              <div className="p-4 bg-virgo-navy-900/50 rounded-lg border border-virgo-navy-600">
                <h4 className="text-sm font-semibold text-virgo-gold-400 uppercase tracking-wider mb-2">Client Portal</h4>
                <div className="flex items-center gap-4 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    contact.portal_enabled
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                  }`}>
                    {contact.portal_enabled ? 'Portal Enabled' : 'Portal Disabled'}
                  </span>
                  {contact.portal_enabled && contact.last_portal_login && (
                    <span className="text-sm text-virgo-teal-400">
                      Last login: {new Date(contact.last_portal_login).toLocaleDateString()}
                    </span>
                  )}
                  {!contact.portal_enabled && (
                    <button
                      onClick={handleEnablePortal}
                      disabled={enablingPortal || !contact.primary_email_address}
                      className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                      title={!contact.primary_email_address ? 'Email address required to enable portal' : ''}
                    >
                      {enablingPortal ? 'Enabling...' : 'Enable Portal'}
                    </button>
                  )}
                  {contact.portal_enabled && (
                    <button
                      onClick={handleSendPasswordReset}
                      disabled={sendingPasswordReset}
                      className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      {sendingPasswordReset ? 'Sending...' : (contact.last_portal_login ? 'Send Password Reset' : 'Resend Invitation')}
                    </button>
                  )}
                </div>
                {!contact.portal_enabled && !contact.primary_email_address && (
                  <p className="mt-2 text-xs text-amber-400">
                    Add an email address to enable portal access
                  </p>
                )}
                {contact.portal_enabled && !contact.last_portal_login && (
                  <p className="mt-2 text-xs text-virgo-teal-500">
                    Client has not logged in yet. An invitation email was sent to set up their password.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Two-way sync info notice */}
          {isEditingDetails && (
            <div className="px-6 pb-6">
              <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-500/30">
                <p className="text-sm text-blue-300">
                  <strong>Two-way Sync:</strong> Changes made here will automatically update the contact in the Contacts module.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        !isSearching && (
          <div className="bg-virgo-navy-800/50 rounded-lg border border-virgo-teal-700/30 p-12 text-center">
            <div className="text-6xl mb-4">👤</div>
            <h3 className="text-xl font-semibold text-white mb-2">No Contact Assigned</h3>
            <p className="text-virgo-teal-400 mb-6">
              This case doesn't have a contact assigned yet. Assign a contact to link this case to a client.
            </p>
            <button
              onClick={() => setIsSearching(true)}
              className="px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Assign Contact
            </button>
          </div>
        )
      )}
    </div>
  );
};

export default CaseContact;
